<script lang="ts" setup>
import BestandenUploaden from '@shared/form/BestandenUploaden/BestandenUploaden.vue';
import {
  useBestandenBestandenOphalen,
  useBestandenBestandVerwijderen,
} from '@services/communicatie-public/orval-api/bestanden-api/bestanden-api';
import { KantoorDocumentReference } from '@communicatie-lib/EntityReference';
import { ref, toRef, unref } from 'vue';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import VStatusTag from '@shared/component/VStatusTag/VStatusTag.vue';
import VCollapsibleContentBox from '@shared/component/VCollapsibleContentBox/VCollapsibleContentBox.vue';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import LeesMeerText from '@shared/component/ReadMore/LeesMeerText.vue';
import type { DocumentenDocumentenGetDocumentenDto as DocumentDto } from '@services/communicatie-public/orval-api/model/documentenDocumentenGetDocumentenDto';
import type { DocumentenDomainEntitiesMetadataLabelEnum as MetadataLabel } from '@services/communicatie-public/orval-api/model/documentenDomainEntitiesMetadataLabelEnum';
import VImage from '@shared/component/VImage/VImage.vue';
import { FontAwesomeIcon, getFontAwesomeIconFromMimeType } from '@shared/plugin/icons';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VButton from '@shared/component/VButton/VButton.vue';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';

interface Props {
  adviseurnummer: number;
}

const props = defineProps<Props>();

const isBestandenOpen = ref(false);
const reference = new KantoorDocumentReference(toRef(() => props.adviseurnummer));

function getMetadata(bestand: DocumentDto, label: MetadataLabel) {
  return bestand.metadata.find((m) => m.label === label)?.waarde ?? '';
}
const {
  data: bestanden,
  error,
  isPending: isPendingGetBestanden,
  refetch: bestandenOpnieuwOphalen,
} = useBestandenBestandenOphalen(reference.entityType, reference.entityId, {
  query: {
    select: (r) =>
      r.result.map((document) => ({
        guid: document.guid,
        bestandsnaam: document.bestandsnaam,
        url: document.url,
        mimeType: getMetadata(document, 'Bestandstype'),
        toelichting: getMetadata(document, 'Toelichting'),
      })),
  },
});
useWatchErrorHandler(error);

const { mutate: remove, error: verwijderError } = useBestandenBestandVerwijderen({
  mutation: {
    async onSuccess() {
      await bestandenOpnieuwOphalen();
    },
  },
});
useWatchErrorHandler(verwijderError);

function isImageType(mimeType: string) {
  return mimeType.substring(0, mimeType.indexOf('/')) === 'image';
}

function verwijder(id: string) {
  remove({ type: reference.entityType, id: unref(reference.entityId), documentId: id });
}
</script>

<template>
  <VCollapsibleContentBox v-model:open="isBestandenOpen" class="w-full">
    <template #header>
      <div class="flex flex-row items-center gap-2">
        Bestanden
        <VStatusTag v-if="!isBestandenOpen && bestanden?.length" color="grey">
          {{ bestanden?.length }}
        </VStatusTag>
      </div>
    </template>
    <template #header-end>
      <BestandenUploaden
        entity-type="KantoorDocument"
        :entity-id="props.adviseurnummer.toString()"
      />
    </template>
    <div>
      <VSkeleton v-if="isPendingGetBestanden" :count="1" height="lg" />
      <VDataTable v-else :value="bestanden" borderless>
        <VColumn field="" header="">
          <template #body="{ data }">
            <VImage
              v-if="isImageType(data.mimeType)"
              :src="data.url"
              :alt="`Preview van ${data.bestandsnaam}`"
              image-class="h-6 object-contain w-6 max-w-none"
              preview
            />
            <a v-else download :href="data.url" rel="noopener noreferrer" target="_blank">
              <FontAwesomeIcon
                class="w-6"
                :icon="['far', getFontAwesomeIconFromMimeType(data.mimeType)]"
              />
            </a>
          </template>
        </VColumn>

        <VColumn field="bestandsnaam" header="Bestand">
          <template #body="{ data }">
            <a
              download
              :href="data.url"
              rel="noopener noreferrer"
              target="_blank"
              :title="data.bestandsnaam"
              class="block max-w-[28ch] truncate"
            >
              {{ data.bestandsnaam }}
            </a>
          </template>
        </VColumn>

        <VColumn field="toelichting" header="Toelichting">
          <template #body="{ data }">
            <LeesMeerText :text="data.toelichting" />
          </template>
        </VColumn>

        <VColumn field="" header="">
          <template #body="{ data }">
            <VButton
              type="danger"
              inverted
              icon-only
              aria-label="Verwijder bestand"
              @click="verwijder(data.guid)"
              :prepend-icon="faTrashAlt"
            />
          </template>
        </VColumn>
      </VDataTable>
    </div>
  </VCollapsibleContentBox>
</template>
